import {
  type BriefingFacts,
  type CapacityOperands,
  briefingFactsSchema,
  capacityOperandsSchema,
} from "@hakili/contracts";
import { formatDateTime, formatMoney, t } from "@hakili/i18n";
import { Button, Callout, CheckIcon, ProgressBar } from "@hakili/ui";
import { AlertTriangle, ChevronDown, ChevronRight, Info } from "lucide-react";
import { type ReactElement, useState } from "react";

import { LabelledSentence } from "../labelled-sentence.js";
import { DASH, fill } from "./dossiers.js";
import { incompleteCauseOf } from "./incomplete-cause.js";

/**
 * Round `U-2`, `M3` , **Hakili's analysis as the bank reads it** (WF-BNK-04,
 * WF-BNK-05, WF-BNK-09; `hakili-bank.jsx`'s « Note de solidité » block and the
 * composer's « Proposition de Hakili » line; OD-37's law on the bank's side).
 *
 * The door (`GET /bank/files/{id}/underwriting`) gives the run: the sixteen
 * proposals with their evidence and the analyst's act beside each, the grade
 * with its five components, the grid's offer, the avis, the trail. This file
 * holds the view's shape, its parser and the pieces the review, the composer
 * and the chef's card mount: the proposal chip on a point, the grade card,
 * the avis panel, the trail. Every act is a door's (`App.tsx`); nothing here
 * decides.
 */
export interface AnalysisActView {
  readonly act: "confirmee" | "corrigee";
  readonly value: string | null;
  readonly byName: string;
  readonly actedAt: string;
  /** `OD-37` round 10b (§8.2): « criblage effectué » and « outil ou source » on an act on P14; `null` on every other act. */
  readonly screening: { readonly done: boolean; readonly source: string | null } | null;
}

export interface AnalysisPointView {
  readonly code: string;
  readonly mode: "arithmetic" | "reading";
  readonly verdict: "conforme" | "non_conforme" | "incertain";
  readonly reason: string;
  /** `ARC1-DATA`: the pièces this point read, by code, from the evidence the run STORED; `[]` for figures or no evidence. */
  readonly documentCodes: readonly string[];
  readonly evidence:
    | {
        readonly kind: "document";
        readonly documentCode: string;
        /** `BANK-AGENT`: the pièce by name, from the door; the code is kept for the record and never printed. */
        readonly documentLabel: string | null;
        readonly page: number;
        readonly box: { x: number; y: number; w: number; h: number } | null;
        /** `ARC1-DATA`: the pièce's stored receipt time (an ISO instant), `null` when it is not received or the door is older. */
        readonly receivedAt: string | null;
      }
    | {
        readonly kind: "figures";
        readonly compared: Readonly<Record<string, number | string | null>>;
        /** `QUOTITE-OPERANDS`: on the quotité point, the operands it was computed from, on the contract; `null` on an older door or a malformed object. */
        readonly operands: CapacityOperands | null;
      }
    | null;
  readonly act: AnalysisActView | null;
  /** `POLICY-RERUN`: her act on the analysis before, on a point the bank's new policy changed; awaiting her confirmation. */
  readonly earlierAct?: AnalysisActView | null;
}

export interface AnalysisGradeView {
  /** `BANK-IDS-2`: the grid's version as a number (2; 1 on a run begun before round 8) , the card prints « grille de notation v2 ». */
  readonly gridVersion: number;
  /** `null` while the note is incomplète: the capacity could not be computed, so there is no letter. */
  readonly grade: string | null;
  readonly average: number | null;
  /** `CAPACITE-NON-CALCULEE`: « note incomplète » with the cause in Hakili's words; `null` when the capacity was computed. */
  readonly incomplete: { readonly cause: string; readonly words: string } | null;
  readonly components: readonly {
    readonly key: string;
    /** `null` for the Capacité that could not be computed: it has no score. */
    readonly score: number | null;
    /** The printed calculation, for the analyst to redo by hand. */
    readonly operands: string;
    readonly note: string;
  }[];
  /** « moyenne 68,0 ≥ 65 : B+ », the threshold crossed; `null` on a v1 run. */
  readonly thresholdLine: string | null;
  /** « mensualité au taux de la ligne la plus haute de la grille (B, 11,2 %) = 132 260 »; `null` when unread or v1. */
  readonly instalmentLine: string | null;
  /** A grade with a « non lu » is provisional: the word rides the letter and Hakili proposes no offer. */
  readonly provisional: boolean;
  readonly unread: readonly { readonly component: string; readonly reading: string | null }[];
  /** `U-3`, `M0 (a)`: the grade re-derived from her acts, the composer's; `null` when the door gives none. */
  readonly rederived: { readonly grade: string | null; readonly correctedCodes: readonly string[] } | null;
  /** `OD-37` round 10b (§9.4): the analyst's latest second look beside the grade, with its count; `null` while none. */
  readonly secondLook: AnalysisSecondLookView | null;
  /** `SECOND-LOOK-CARRY`: her look on the grade before, when the bank's new policy changed the grade; awaiting her word, not counted. */
  readonly earlierSecondLook?: AnalysisSecondLookView | null;
}

/** `OD-37` round 10b (§9.4): one disagreement of the second look. */
export interface AnalysisDisagreementView {
  readonly component: string;
  readonly ordinal: number;
  readonly findingId: string | null;
  readonly motif: string;
  readonly phrase: string | null;
}

export interface AnalysisSecondLookView {
  readonly accord: boolean;
  readonly disagreements: readonly AnalysisDisagreementView[];
  readonly byName: string;
  readonly actedAt: string;
  readonly count: number;
}

/** The body the second-look door takes: no author, the session's is hers. */
export interface SecondLookBody {
  readonly accord: boolean;
  readonly disagreements?: readonly { component: number; motif: string; phrase?: string }[];
}

/** `TERM-FIT`: the four reasons a proposed term can have, as the door types them. */
export type TermBasis = "demandee" | "rallongee_capacite" | "ramenee_contrat" | "ramenee_retraite";
const TERM_BASES: readonly TermBasis[] = [
  "demandee",
  "rallongee_capacite",
  "ramenee_contrat",
  "ramenee_retraite",
];

/** `BANK-IDS-2`: the letters of the ladder a grid can lack, best first. C is never reported (ruling of 22 October 2026). */
const GRADE_LADDER: readonly string[] = ["A+", "A", "B+", "B"];

export interface AnalysisOfferView {
  /** `U-3`, `M0 (a)`: the grade the run PROPOSED the offer at , the line names it, whatever she re-derived since. */
  readonly gradeProposed: string | null;
  readonly gridRatePct: string;
  readonly durationMonths: number;
  readonly instalmentAmount: number;
  readonly taegPct: string;
  readonly requiresCommittee: boolean;
  readonly act: AnalysisActView | null;
  /** `TERM-FIT`: why this term (`termBasis`, typed) in the bank's words (`termBasisWords`); `null` on a door that does not send them. */
  readonly termBasis: TermBasis | null;
  readonly termBasisWords: string | null;
}

export interface AnalysisOpinionView {
  readonly text: string;
  readonly recommendation: "favorable" | "reserve" | "defavorable";
  /** `POLICY-RERUN`: « À relire : la politique de la banque a changé le {date}. », the door's words, until the analyst acts on the avis. */
  readonly notice?: string | null;
  /** `OFFER-INSURANCE-ONCE-B`: the same mark, on the avis that cites the superseded reste à vivre. */
  readonly superseded?: string | null;
  readonly act: AnalysisActView | null;
}

export interface AnalysisStepView {
  readonly ordinal: number;
  readonly kind: string;
  readonly startedAt: string;
  readonly endedAt: string;
  readonly failure: string | null;
}

/** `F-2`: one line of the register of findings, in the server's words , never its code or the id of the step. */
export interface AnalysisFindingView {
  readonly severity: "bloquant" | "a_lever" | "signale";
  readonly reason: string;
  /** The pièce the finding is tied to (`evidence.kind === "document"`), by code; `null` for figures or no evidence. */
  readonly documentCode: string | null;
}

export interface AnalysisView {
  /** `documentsReadAt` (`ARC2-DATA`): the instant the pièces were really read, as the door gives it; `null` where it does not know. */
  readonly run: {
    readonly status: string;
    readonly outcome: string | null;
    readonly documentsReadAt: string | null;
    /** `POINTS-SUMMARY` (lane AD): the points the run should carry and how many of the produced ones carry their evidence; absent on an older door. */
    readonly points?: { readonly expected: number; readonly withEvidence: number };
  };
  readonly points: readonly AnalysisPointView[];
  /** `F-2`: the register the bay folds under « constats »; `null` on a run begun before the register. */
  readonly registre: readonly AnalysisFindingView[] | null;
  readonly grade: AnalysisGradeView | null;
  readonly offer: AnalysisOfferView | null;
  /** `BANK-IDS-2`: the ladder's letters the bank's grid has no row for, best first; empty where the grid is full or absent. */
  readonly gridMissingGrades: readonly string[];
  /** `OD-37` round 8: why the offer step proposed nothing , « note provisoire, 5 lectures manquantes »; `null` when it proposed. */
  readonly offerReason: string | null;
  readonly opinion: AnalysisOpinionView | null;
  /** `OD-37` round 6 (ruling Q15): the commitments figure retained for capacity, with both operands; `null` on an older run. */
  readonly commitments: {
    readonly sentence: string;
    readonly staleNotes: readonly string[];
  } | null;
  /** `OD-37` round 9c-2b (L-6): the readings behind the number; `null` on a run begun before the counters. */
  readonly card: AnalysisCardView | null;
  readonly termNote: string | null;
  readonly failure: string | null;
  readonly steps: readonly AnalysisStepView[];
  /** `BRIEFING-FIELDS` (#642): the four facts of the nouveau briefing as the server read them; `null` on a run stored before them or in another shape. */
  readonly briefing: BriefingFacts | null;
}

export interface AnalysisResteAVivreView {
  readonly amount: number;
  readonly sharePct: string;
  readonly operands: string;
  /** `OFFER-INSURANCE-ONCE-B`: the server's superseded mark on a figure computed before the cure (APPROVED words); `null` or absent otherwise. */
  readonly superseded?: string | null;
}

export interface AnalysisCardView {
  readonly readings: readonly {
    readonly code: string;
    readonly verdict: string;
    readonly reason: string;
    readonly operands: readonly string[];
  }[];
  readonly capacityAtFile: number | null;
  readonly capacityObserved: { readonly amount: number; readonly derivation: string } | null;
  readonly fees: {
    readonly average: number | null;
    readonly perMonth: readonly { readonly month: string; readonly label: string; readonly total: number }[];
  } | null;
  readonly resteAVivre: {
    readonly recommended: AnalysisResteAVivreView;
    readonly requested: AnalysisResteAVivreView | null;
  } | null;
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;
const str = (value: unknown): string | null => (typeof value === "string" ? value : null);
const num = (value: unknown): number | null => (typeof value === "number" ? value : null);

const actFrom = (raw: unknown): AnalysisActView | null => {
  const row = asRecord(raw);
  const act = str(row?.["act"]);
  const byName = str(row?.["byName"]);
  if (row === null || (act !== "confirmee" && act !== "corrigee") || byName === null) return null;
  const screening = asRecord(row["screening"]);
  return {
    act,
    value: str(row["value"]),
    byName,
    actedAt: str(row["actedAt"]) ?? "",
    screening:
      screening !== null && typeof screening["done"] === "boolean"
        ? { done: screening["done"], source: str(screening["source"]) }
        : null,
  };
};

const secondLookFrom = (raw: unknown): AnalysisSecondLookView | null => {
  const row = asRecord(raw);
  const byName = str(row?.["byName"]);
  if (row === null || typeof row["accord"] !== "boolean" || byName === null) return null;
  return {
    accord: row["accord"],
    disagreements: Array.isArray(row["disagreements"])
      ? row["disagreements"].flatMap((entry) => {
          const one = asRecord(entry);
          const component = str(one?.["component"]);
          const motif = str(one?.["motif"]);
          if (one === null || component === null || motif === null) return [];
          return [
            {
              component,
              ordinal: num(one["ordinal"]) ?? 0,
              findingId: str(one["findingId"]),
              motif,
              phrase: str(one["phrase"]),
            },
          ];
        })
      : [],
    byName,
    actedAt: str(row["actedAt"]) ?? "",
    count: num(row["count"]) ?? 1,
  };
};

const VERDICTS = ["conforme", "non_conforme", "incertain"] as const;
const RECOMMENDATIONS = ["favorable", "reserve", "defavorable"] as const;
const SEVERITIES = ["bloquant", "a_lever", "signale"] as const;

/** The grade as the door gives it (`GET /bank/files/{id}/underwriting`), for the analyst's card and the chef's read alike. */
export function gradeFrom(grade: Record<string, unknown> | null): AnalysisGradeView | null {
  const incompleteRow = grade === null ? null : asRecord(grade["incomplete"]);
  const incomplete =
    incompleteRow !== null && str(incompleteRow["words"]) !== null
      ? { cause: str(incompleteRow["cause"]) ?? "", words: str(incompleteRow["words"]) ?? "" }
      : null;
  if (grade === null || (str(grade["grade"]) === null && incomplete === null)) return null;
  return {
    gridVersion: num(grade["gridVersion"]) ?? 1,
    grade: str(grade["grade"]),
    average: num(grade["average"]),
    incomplete,
    thresholdLine: str(grade["thresholdLine"]),
    instalmentLine: str(grade["instalmentLine"]),
    provisional: grade["provisional"] === true,
    unread: Array.isArray(grade["unread"])
      ? grade["unread"].flatMap((raw) => {
          const row = asRecord(raw);
          const component = str(row?.["component"]);
          return component === null ? [] : [{ component, reading: str(row?.["reading"]) }];
        })
      : [],
    rederived: (() => {
      const row = asRecord(grade["rederived"]);
      const letter = str(row?.["grade"]);
      // A rederived grade with no letter is a note incomplète too (the acts do not change that): kept, so the card can compare.
      return row === null || (letter === null && !("grade" in row))
        ? null
        : {
            grade: letter,
            correctedCodes: Array.isArray(row["correctedCodes"])
              ? row["correctedCodes"].filter((one): one is string => typeof one === "string")
              : [],
          };
    })(),
    secondLook: secondLookFrom(grade["secondLook"]),
    earlierSecondLook: secondLookFrom(grade["earlierSecondLook"]),
    components: Array.isArray(grade["components"])
      ? grade["components"].flatMap((raw) => {
          const row = asRecord(raw);
          const key = str(row?.["key"]);
          const score = num(row?.["score"]);
          // The Capacité that could not be computed has no score; any other component without one is dropped as before.
          return key === null || (score === null && key !== "capacity")
            ? []
            : [
                {
                  key,
                  score,
                  operands: str(row?.["operands"]) ?? "",
                  note: str(row?.["note"]) ?? "",
                },
              ];
        })
      : [],
  };
}

function resteFrom(raw: unknown): AnalysisResteAVivreView | null {
  const row = asRecord(raw);
  const amount = num(row?.["amount"]);
  if (row === null || amount === null) return null;
  return {
    amount,
    sharePct: str(row["sharePct"]) ?? "",
    operands: str(row["operands"]) ?? "",
    superseded: str(row["superseded"]),
  };
}

/** `OD-37` round 9c-2b (L-6): the card's parser; every field optional, the card absent when the door gives none. */
export function cardFrom(card: Record<string, unknown> | null): AnalysisCardView | null {
  if (card === null || !Array.isArray(card["readings"])) return null;
  const observed = asRecord(card["capacityObserved"]);
  const fees = asRecord(card["fees"]);
  const reste = asRecord(card["resteAVivre"]);
  const recommended = resteFrom(reste?.["recommended"]);
  return {
    readings: card["readings"].flatMap((raw) => {
      const row = asRecord(raw);
      const code = str(row?.["code"]);
      if (row === null || code === null) return [];
      return [
        {
          code,
          verdict: str(row["verdict"]) ?? "",
          reason: str(row["reason"]) ?? "",
          operands: Array.isArray(row["operands"])
            ? row["operands"].filter((one) => typeof one === "string")
            : [],
        },
      ];
    }),
    capacityAtFile: num(card["capacityAtFile"]),
    capacityObserved:
      observed === null || num(observed["amount"]) === null
        ? null
        : { amount: num(observed["amount"]) ?? 0, derivation: str(observed["derivation"]) ?? "" },
    fees:
      fees === null
        ? null
        : {
            average: num(fees["average"]),
            perMonth: Array.isArray(fees["perMonth"])
              ? fees["perMonth"].flatMap((raw) => {
                  const row = asRecord(raw);
                  const month = str(row?.["month"]);
                  const total = num(row?.["total"]);
                  return month === null || total === null
                    ? []
                    : [{ month, label: str(row?.["label"]) ?? month, total }];
                })
              : [],
          },
    resteAVivre: recommended === null ? null : { recommended, requested: resteFrom(reste?.["requested"]) },
  };
}

export function analysisFrom(answer: unknown): AnalysisView | null {
  const body = asRecord(answer);
  const view = asRecord(body?.["underwriting"]);
  const run = asRecord(view?.["run"]);
  const summary = asRecord(run?.["points"]);
  const runPoints =
    num(summary?.["expected"]) !== null && num(summary?.["withEvidence"]) !== null
      ? { expected: num(summary?.["expected"]) ?? 0, withEvidence: num(summary?.["withEvidence"]) ?? 0 }
      : null;
  if (view === null || run === null || typeof run["status"] !== "string") return null;
  const points: AnalysisPointView[] = Array.isArray(view["points"])
    ? view["points"].flatMap((raw) => {
        const row = asRecord(raw);
        const code = str(row?.["code"]);
        const verdict = VERDICTS.find((one) => one === row?.["verdict"]);
        if (row === null || code === null || verdict === undefined) return [];
        const evidence = asRecord(row["evidence"]);
        return [
          {
            code,
            mode: row["mode"] === "arithmetic" ? "arithmetic" : "reading",
            verdict,
            reason: str(row["reason"]) ?? "",
            documentCodes: Array.isArray(row["documentCodes"])
              ? row["documentCodes"].filter((one): one is string => typeof one === "string")
              : [],
            evidence:
              evidence === null
                ? null
                : evidence["kind"] === "document"
                  ? {
                      kind: "document",
                      documentCode: str(evidence["documentCode"]) ?? "",
                      documentLabel: str(evidence["documentLabel"]),
                      page: num(evidence["page"]) ?? 1,
                      box: (() => {
                        const box = asRecord(evidence["box"]);
                        const x = num(box?.["x"]);
                        const y = num(box?.["y"]);
                        const w = num(box?.["w"]);
                        const h = num(box?.["h"]);
                        return x === null || y === null || w === null || h === null ? null : { x, y, w, h };
                      })(),
                      receivedAt: str(evidence["receivedAt"]),
                    }
                  : {
                      kind: "figures",
                      compared: (asRecord(evidence["compared"]) ?? {}) as Record<
                        string,
                        number | string | null
                      >,
                      operands: (() => {
                        const parsed = capacityOperandsSchema.safeParse(evidence["operands"]);
                        return parsed.success ? parsed.data : null;
                      })(),
                    },
            act: actFrom(row["act"]),
            earlierAct: actFrom(row["earlierAct"]),
          },
        ];
      })
    : [];
  const grade = asRecord(view["grade"]);
  const offer = asRecord(view["offer"]);
  const opinion = asRecord(view["opinion"]);
  const recommendation = RECOMMENDATIONS.find((one) => one === opinion?.["recommendation"]);
  return {
    run: {
      status: str(run["status"]) ?? "",
      outcome: str(run["outcome"]),
      documentsReadAt: str(run["documentsReadAt"]),
      ...(runPoints === null ? {} : { points: runPoints }),
    },
    points,
    registre: Array.isArray(view["registre"])
      ? view["registre"].flatMap((raw) => {
          const row = asRecord(raw);
          const severity = SEVERITIES.find((one) => one === row?.["severityClass"]);
          const reason = str(row?.["reason"]);
          const evidence = asRecord(row?.["evidence"]);
          const documentCode = evidence?.["kind"] === "document" ? str(evidence["documentCode"]) : null;
          return severity === undefined || reason === null ? [] : [{ severity, reason, documentCode }];
        })
      : null,
    grade: gradeFrom(grade),
    briefing: (() => {
      const parsed = briefingFactsSchema.safeParse(view["briefing"]);
      return parsed.success ? parsed.data : null;
    })(),
    gridMissingGrades: GRADE_LADDER.filter(
      (letter) => Array.isArray(view["gridMissingGrades"]) && view["gridMissingGrades"].includes(letter),
    ),
    offerReason: str(view["offerReason"]),
    offer:
      offer === null || str(offer["gridRatePct"]) === null
        ? null
        : {
            gradeProposed: grade === null ? null : str(grade["grade"]),
            gridRatePct: str(offer["gridRatePct"]) ?? "",
            durationMonths: num(offer["durationMonths"]) ?? 0,
            instalmentAmount: num(offer["instalmentAmount"]) ?? 0,
            taegPct: str(offer["taegPct"]) ?? "",
            requiresCommittee: offer["requiresCommittee"] === true,
            act: actFrom(offer["act"]),
            termBasis: TERM_BASES.find((one) => one === offer["termBasis"]) ?? null,
            termBasisWords: str(offer["termBasisWords"]),
          },
    opinion:
      opinion === null || str(opinion["text"]) === null || recommendation === undefined
        ? null
        : {
            text: str(opinion["text"]) ?? "",
            recommendation,
            notice: str(opinion["notice"]),
            superseded: str(opinion["superseded"]),
            act: actFrom(opinion["act"]),
          },
    commitments: (() => {
      const row = asRecord(view["commitments"]);
      const sentence = str(row?.["sentence"]);
      if (row === null || sentence === null) return null;
      const notes = Array.isArray(row["staleNotes"])
        ? row["staleNotes"].filter((one) => typeof one === "string")
        : [];
      return { sentence, staleNotes: notes };
    })(),
    card: cardFrom(asRecord(view["card"])),
    termNote: str(asRecord(view["offer"])?.["termNote"]),
    failure: str(view["failure"]),
    steps: Array.isArray(view["steps"])
      ? view["steps"].flatMap((raw) => {
          const row = asRecord(raw);
          const ordinal = num(row?.["ordinal"]);
          const kind = str(row?.["kind"]);
          if (row === null || ordinal === null || kind === null) return [];
          return [
            {
              ordinal,
              kind,
              startedAt: str(row["startedAt"]) ?? "",
              endedAt: str(row["endedAt"]) ?? "",
              failure: str(row["failure"]),
            },
          ];
        })
      : [],
  };
}

/** The door's R81 sentence beside a null analysis, or the failed step's, as the door gives them. */
export const analysisSentenceFrom = (answer: unknown): string | null => str(asRecord(answer)?.["sentence"]);

const VERDICT_KEYS = new Map<AnalysisPointView["verdict"], Parameters<typeof t>[0]>([
  ["conforme", "bank.hakili.verdictConforme"],
  ["non_conforme", "bank.hakili.verdictNonConforme"],
  ["incertain", "bank.hakili.verdictIncertain"],
]);

const COMPONENT_KEYS = new Map<string, Parameters<typeof t>[0]>([
  ["incomeStability", "bank.hakili.composanteRevenus"],
  ["collateral", "bank.hakili.composanteGaranties"],
  ["bankingHistory", "bank.hakili.composanteHistorique"],
  ["deposit", "bank.hakili.composanteApport"],
  ["capacity", "bank.hakili.composanteCapacite"],
]);

const RECOMMENDATION_KEYS = new Map<AnalysisOpinionView["recommendation"], Parameters<typeof t>[0]>([
  ["favorable", "bank.hakili.avisFavorable"],
  ["reserve", "bank.hakili.avisReserve"],
  ["defavorable", "bank.hakili.avisDefavorable"],
]);

const STEP_KEYS = new Map<string, Parameters<typeof t>[0]>([
  ["points", "bank.hakili.faitPoints"],
  ["grade", "bank.hakili.faitNote"],
  ["offer", "bank.hakili.faitOffre"],
  ["avis", "bank.hakili.faitAvis"],
]);

const READING_KEYS: ReadonlyMap<string, string> = new Map([
  ["incidents", "bank.hakili.lectureIncidents"],
  ["negativeMonths", "bank.hakili.lectureMoisNegatifs"],
  ["salaryRegularity", "bank.hakili.lectureRegularite"],
  ["evidencedApport", "bank.hakili.lectureApportEvidencie"],
  ["bulletins", "bank.hakili.lectureBulletins"],
  ["releves", "bank.hakili.lectureReleves"],
  ["attestationTravail", "bank.hakili.lectureAttestation"],
  ["salaireDeclareObserve", "bank.hakili.lectureSalaire"],
  ["employmentQuality", "bank.hakili.lectureEmploi"],
]);

/** A reading of the grille by its name, for the list of what is unread. */
const readingLabel = (reading: string): string =>
  t(READING_KEYS.get(reading) ?? "bank.hakili.lectureIncidents");

/** The component's name as the drawing prints it, from its key. */
export const componentLabel = (key: string): string =>
  t(COMPONENT_KEYS.get(key) ?? "bank.hakili.composanteCapacite");

/** `BANK-AGENT`: what is missing, as a full sentence , never a bare label after the stock sentence. */
const missingSentence = (one: { readonly component: string; readonly reading: string | null }): string =>
  one.reading !== null
    ? fill("bank.hakili.provisoireLecture", { lecture: readingLabel(one.reading) })
    : fill("bank.hakili.provisoireComposante", { composante: componentLabel(one.component).toLowerCase() });

export const verdictLabel = (verdict: AnalysisPointView["verdict"]): string =>
  t(VERDICT_KEYS.get(verdict) ?? "bank.hakili.verdictIncertain");

/** A component's score as the bank reads it: one decimal and the French comma (« 71,4 », « 100,0 »), never the dot. */
const scoreWords = (score: number): string =>
  new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(score);

const when = (iso: string): string => (Number.isNaN(Date.parse(iso)) ? DASH : formatDateTime(new Date(iso)));

/** Where the proposal was read: the pièce and its page, the box drawn as a percentage where the model gave one (R81: no viewer, the place named). */
export function evidenceWords(point: AnalysisPointView): string | null {
  if (point.evidence === null) return null;
  if (point.evidence.kind === "document") {
    const base = fill("bank.hakili.preuvePiece", {
      piece: point.evidence.documentLabel ?? t("bank.hakili.preuvePieceSansNom"),
      page: String(point.evidence.page),
    });
    return point.evidence.box === null
      ? base
      : `${base} · ${fill("bank.hakili.preuveZone", { x: String(point.evidence.box.x), y: String(point.evidence.box.y) })}`;
  }
  // `BANK-AGENT`: the door hands the figures under their French names and with their units; a figure left unread is not printed.
  const figures = Object.entries(point.evidence.compared)
    .filter(([, value]) => value !== null)
    .map(([key, value]) => `${key} ${String(value)}`)
    .join(" · ");
  // `QUOTITE-OPERANDS`: the grade's own figure, under its own label , a computed one only, never a guess.
  const used = point.evidence.operands?.capacityUsedPct ?? null;
  const usedWords =
    used === null
      ? ""
      : `${figures === "" ? "" : " · "}${t("bank.hakili.quotiteUtilisee")} ${tenthsWords(used)} %`;
  return figures === "" && usedWords === ""
    ? t("bank.hakili.preuveChiffres")
    : `${t("bank.hakili.preuveChiffres")} : ${figures}${usedWords}`;
}

/** A percent the door already rounded to one decimal, with a French comma (« 98,0 »): written out, never rounded or float-formatted here. */
const tenthsWords = (value: number): string => {
  const [whole = "0", fraction = "0"] = String(value).split(".");
  return `${whole},${fraction}`;
};

/** The act's line under a point, an offer or an avis: « Proposition confirmée par … le … ». */
export function actWords(act: AnalysisActView): string {
  const line = fill(act.act === "confirmee" ? "bank.hakili.acteConfirme" : "bank.hakili.acteCorrige", {
    nom: act.byName,
    quand: when(act.actedAt),
  });
  // `OD-37` round 10b (§8.2): on P14 the line also says whether she screened, and by what means.
  if (act.screening === null) return line;
  return `${line} ${
    act.screening.done
      ? fill("bank.hakili.acteCriblageOui", { source: act.screening.source ?? "" })
      : t("bank.hakili.acteCriblageNon")
  }`;
}

/**
 * The drawing's « Note de solidité » block, **in the bay** (`F-2`): the ring and the letter, five bars, each with its basis.
 *
 * Four forms, all from the server's answer and none computed here (D-1021-4): **final** (letter, ring, mean), **provisional**
 * (the letter keeps its word, dashed ring), **incomplete** (NO letter, no ring, no mean: « Capacité non calculée : <cause>
 * » in the server's words, and the link to « Politique » only for the grid cause and only for a reader who has that
 * screen), and the **partial grid**, which is a final note whose instalment line names the highest row the server used.
 * The rule's name (`note-solidite-v2`) is not printed: it is an identifier of the code, not a word of the bank (BANK-IDS).
 */
/** « B+ et B », « A, B+ et B »: the letters as a person says them. */
const frenchList = (items: readonly string[]): string =>
  items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} et ${items[items.length - 1] ?? ""}`;

export function GradeCard(props: {
  readonly grade: AnalysisGradeView | null;
  /** `BANK-IDS-2`: the ladder's letters the bank's grid has no rate for (`AnalysisView.gridMissingGrades`); empty draws nothing. */
  readonly missingGrades?: readonly string[];
  readonly sentence?: string | null;
  /**
   * `ARC2-BAY` (D2 frame 18): the row of the grid the Capacité was computed at when the grid lacks letters , the quotité point's own operands
   * (`rateSource`, `ratePct`), never guessed; `null` where the door sends none.
   */
  readonly capacityRate?: { readonly grade: string; readonly ratePct: string } | null;
  /** `F-2`: goes to « Politique ». Given only to a reader who has that screen (the chef); drawn only for the grid cause. */
  readonly onPolitique?: () => void;
}): ReactElement {
  const { grade } = props;

  const missing = props.missingGrades ?? [];
  const incomplete = grade?.incomplete ?? null;
  const cause = incompleteCauseOf(incomplete);
  // `BAY-NOTE-BOX-WORDS`: D2 draws its sentence for ONE unread reading, with the score the server counts for that component (the bar's own figure); with none served it falls back to the stock words.
  const firstUnread = grade?.unread.length === 1 ? grade.unread[0] : undefined;
  const unreadScore =
    firstUnread === undefined
      ? undefined
      : grade?.components.find((one) => one.key === firstUnread.component)?.score;
  const oneUnread =
    firstUnread === undefined || unreadScore === undefined || unreadScore === null
      ? null
      : { component: firstUnread.component, score: unreadScore };
  const state =
    grade === null
      ? "absent"
      : incomplete !== null
        ? "incomplete"
        : grade.provisional
          ? "provisional"
          : "final";
  return (
    <section data-bay="grade" data-state={state} data-testid="bank-hakili-note">
      {/* D2 draws no heading between the head and the ring (`bay.js` gradeBlock): it is kept for a screen reader, not drawn. The kit takes it out
          of the flow (`absolute`, `bank-theme.css:1466`) and the section is `display: contents`, so a positioned box of its own anchors it: with
          none it is placed against the page, and where the stacked bay starts below the window (1099 x 800) its 1 px pushes the document past it. */}
      <span className="relative block">
        <h3 data-bay="h3" data-sr="">
          {t("bank.hakili.noteTitre")}
        </h3>
      </span>
      {grade === null ? (
        <p data-bay="note" data-testid="bank-hakili-note-absente">
          {props.sentence ?? t("bank.hakili.noteAbsente")}
        </p>
      ) : (
        <>
          <div data-bay="gr">
            <div data-bay="ringcol">
              <div data-bay="ring" data-state={state}>
                {incomplete !== null ? null : (
                  <svg viewBox="0 0 120 120" aria-hidden="true" data-bay="ring-svg">
                    <circle cx="60" cy="60" r="50" fill="none" data-bay="ring-track" strokeWidth="10" />
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      data-bay="ring-arc"
                      strokeWidth="10"
                      strokeLinecap="round"
                      pathLength="100"
                      strokeDasharray={`${String(grade.average ?? 0)} 100`}
                      transform="rotate(-90 60 60)"
                    />
                  </svg>
                )}
                <div data-bay="ring-centre">
                  {incomplete === null ? null : (
                    <AlertTriangle className="h-6 w-6" aria-hidden="true" data-bay="ring-alerte" />
                  )}
                  <b data-bay="letter" data-testid="bank-hakili-note-lettre">
                    {incomplete !== null ? t("bank.hakili.noteIncompleteRond") : (grade.grade ?? DASH)}
                  </b>
                  {incomplete === null && !grade.provisional && grade.average !== null ? (
                    <span data-bay="mean" data-testid="bank-hakili-note-moyenne">
                      {scoreWords(grade.average)}
                    </span>
                  ) : null}
                </div>
              </div>
              {grade.provisional && incomplete === null ? (
                <span data-bay="chip">{t("bank.hakili.noteProvisoire")}</span>
              ) : null}
            </div>
            <div data-bay="comps">
              {grade.components.map((one) => {
                // D2 `.cbr.inc` (frame 13): a provisional note names the component it could not read , a dashed trace, its figure in the
                // warning tone, and « non lu · compte {figure} » under it. Only the components the door lists as unread.
                const unreadRow =
                  incomplete === null &&
                  grade.provisional &&
                  grade.unread.some((u) => u.component === one.key);
                return (
                  <div key={one.key} data-bay="comp" data-testid={`bank-hakili-composante-${one.key}`}>
                    <div data-bay="comp-row">
                      <span data-bay="comp-name">{componentLabel(one.key).split(" ")[0]}</span>
                      <b data-bay="comp-score">{one.score === null ? DASH : scoreWords(one.score)}</b>
                    </div>
                    {one.score === null || unreadRow ? (
                      // D2 `.cbr.inc`: a component the note did not read has a dashed trace where its bar would be, never a zero.
                      <span data-bay="comp-trace" data-unread="" aria-hidden="true" />
                    ) : (
                      <ProgressBar label={componentLabel(one.key)} value={one.score} tone="brand" />
                    )}
                    {one.key === "capacity" && incomplete !== null ? (
                      // `CAPACITE-NON-CALCULEE`: no score and no bar; the state and the cause, in Hakili's words (the API's, the same on the console card).
                      // `F-2d` (J1): the label, a full stop, then the server's sentence with a capital and its stop (the one joiner, lane A's).
                      <p data-bay="comp-note" data-testid="bank-hakili-capacite-non-calculee">
                        {cause === null ? (
                          <LabelledSentence
                            label={t("bank.hakili.capaciteNonCalculee")}
                            words={incomplete.words}
                          />
                        ) : (
                          fill("bank.bay.compNonCalculee", { court: cause.court })
                        )}
                      </p>
                    ) : unreadRow && one.score !== null ? (
                      <p data-bay="comp-note">
                        {fill("bank.bay.compNonLu", { score: scoreWords(one.score) })}
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
          {/* D2 `bay.js` gradeBlock: the notes follow the ring, then the fold: the grid's missing letters, the provisional note, the incomplete note. */}
          {missing.length === 0 ? null : (
            <p data-bay="note" data-kind="grille" data-testid="bank-hakili-grille-incomplete">
              <strong>{t("bank.bay.grilleIncompleteTitre")}</strong>{" "}
              {props.capacityRate === null || props.capacityRate === undefined
                ? null
                : `${fill("bank.bay.grilleIncompleteTaux", {
                    note: props.capacityRate.grade,
                    taux: props.capacityRate.ratePct.replace(".", ","),
                  })} `}
              {fill(missing.length === 1 ? "bank.bay.grilleIncompleteUne" : "bank.bay.grilleIncompleteDes", {
                notes: frenchList(missing),
              })}
            </p>
          )}
          {grade.provisional && incomplete === null ? (
            <p data-bay="note" data-kind="provisoire" data-testid="bank-hakili-note-provisoire">
              <strong>{t("bank.hakili.noteProvisoireTitre")}</strong>{" "}
              {oneUnread === null ? (
                <>
                  {grade.unread.length === 1
                    ? t("bank.hakili.noteProvisoireUne")
                    : fill("bank.hakili.noteProvisoireDes", { n: String(grade.unread.length) })}{" "}
                  {grade.unread.map(missingSentence).join(" ")}
                </>
              ) : (
                // `BAY-NOTE-BOX-WORDS` (S5, D2 `bay.js:42`, `.provnote`): D2's own sentence for the one unread reading, the figures the served ones.
                fill("bank.hakili.noteProvisoireLectureNonFaite", {
                  n: String(grade.unread.length),
                  composante: componentLabel(oneUnread.component),
                  score: scoreWords(oneUnread.score),
                })
              )}
            </p>
          ) : null}
          {incomplete !== null ? (
            <div data-bay="note" data-kind="incomplete" data-testid="bank-hakili-note-incomplete">
              {cause === null ? (
                <p>{t("bank.hakili.noteIncompleteExplication")}</p>
              ) : (
                <p>
                  <strong>{fill("bank.hakili.causeCarteTitre", { court: cause.court })}</strong>{" "}
                  {cause.phrase} {t("bank.hakili.causeSuite")}
                </p>
              )}
              {props.onPolitique !== undefined && incomplete.cause === "grille" ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={props.onPolitique}
                  data-testid="bank-hakili-note-politique"
                >
                  {t("bank.composer.politiqueLien")}
                </Button>
              ) : null}
            </div>
          ) : null}
          {/* `ARC2-BAY` (D2 `bay.js` gradeBlock): in every context the ring and the five bars carry a name and a score; every operand and note,
              and the mean with the grid's version, fold under « Voir le calcul de la note ». What is missing is never folded away. */}
          <details data-bay="calc" data-testid="bank-hakili-calcul">
            <summary>{t("bank.feuille.titre")}</summary>
            {/* D2 lists the five, each with its score and, under it, how it was read. */}
            {grade.components.map((one) => (
              <div key={one.key} data-bay="calc-comp">
                <b data-bay="comp-name">{componentLabel(one.key)}</b>{" "}
                <span data-bay="comp-score" data-testid={`bank-hakili-calcul-${one.key}`}>
                  {one.score === null ? t("bank.hakili.capaciteNonCalculeeMot") : scoreWords(one.score)}
                </span>
                {one.operands === "" ? null : <p data-bay="comp-note">{one.operands}</p>}
                {one.note === "" ? null : <p data-bay="comp-note">{one.note}</p>}
              </div>
            ))}
            <p data-bay="meta" data-testid="bank-hakili-calcul-pied">
              {incomplete !== null
                ? fill("bank.hakili.calculPiedIncomplete", { version: String(grade.gridVersion) })
                : fill("bank.hakili.calculPied", {
                    moyenne: grade.average === null ? DASH : scoreWords(grade.average),
                    note: grade.grade ?? DASH,
                    provisoire: grade.provisional ? t("bank.hakili.calculPiedProvisoire") : "",
                    version: String(grade.gridVersion),
                  })}
            </p>
          </details>
          {incomplete === null && grade.rederived !== null && grade.rederived.grade !== grade.grade ? (
            <p data-bay="note" data-testid="bank-hakili-note-rederivee">
              {fill("bank.hakili.noteRederivee", { note: grade.rederived.grade ?? DASH })}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

/** The closed list of motifs of workflow §9.4, with the words the analyst reads. */
const SECOND_LOOK_MOTIFS: readonly (readonly [string, string])[] = [
  ["composante_sous_estimee", "bank.hakili.regardMotifSous"],
  ["composante_surestimee", "bank.hakili.regardMotifSur"],
  ["constat_non_reflete", "bank.hakili.regardMotifConstat"],
  ["seuil_trop_strict", "bank.hakili.regardMotifStrict"],
  ["seuil_trop_souple", "bank.hakili.regardMotifSouple"],
  ["autre", "bank.hakili.regardMotifAutre"],
];
/**
 * The ONE place the second look's line is built (the analyst's card and the chef's decision screen both render through
 * `SecondLookRecord`): « <famille> : <motif>. <Phrase> ». The motif after the colon starts lower case, but only when its
 * second letter is lower case too, so an acronym (DTI, CNSS) is never broken; the sentence after the full stop starts with a
 * capital, and one that already does is left as it is (architect's ruling, the designer's recorded text).
 */
export const secondLookLine = (family: string, motif: string, phrase: string | null): string => {
  const motifText = /^\p{Lu}\p{Ll}/u.test(motif)
    ? `${motif.charAt(0).toLowerCase()}${motif.slice(1)}`
    : motif;
  const sentence = phrase === null ? "" : ` ${phrase.charAt(0).toUpperCase()}${phrase.slice(1)}`;
  return `${family} : ${motifText}.${sentence}`;
};
const motifWords = (motif: string): string => {
  const key = SECOND_LOOK_MOTIFS.find(([value]) => value === motif)?.[1];
  return key === undefined ? motif : t(key);
};

/**
 * `OD-37` round 10b (§9.4, D-4) and round 10c: **the analyst's recorded second look, read only.** Her name, the date, her
 * answer, each disagreement with its component, motif and phrase, and the count of looks on the file. It is the one
 * rendering of the look: the analyst's card shows it above her form, the chef de crédit's decision screen shows it and
 * nothing else. No control here posts anything.
 */
export function SecondLookRecord(props: {
  readonly look: AnalysisSecondLookView;
  /** `F-2`: drawn on the dark bay (its own hooks) instead of on the page. */
  readonly onBay?: boolean;
}): ReactElement {
  const { look } = props;
  if (props.onBay === true) {
    return (
      <div data-bay="regard-fait" data-testid="bank-hakili-regard-fait">
        {/* D2 `bay.js` secondLook: a check, then « Seconde lecture de {nom} le {quand} : d'accord avec la note. », as a bay note. */}
        <p data-bay="meta">
          <CheckIcon />{" "}
          {fill(look.accord ? "bank.hakili.regardFaitOui" : "bank.hakili.regardFaitNon", {
            nom: look.byName,
            quand: when(look.actedAt),
          })}
        </p>
        {look.disagreements.map((one) => (
          <p key={`${one.component}-${one.motif}`} data-bay="comp-note">
            {secondLookLine(componentLabel(one.component), motifWords(one.motif), one.phrase)}
          </p>
        ))}
        {look.count > 1 ? (
          <p data-bay="comp-note">{fill("bank.hakili.regardFaitNombre", { n: String(look.count) })}</p>
        ) : null}
      </div>
    );
  }
  return (
    <div className="mb-3 text-sm text-gray-700" data-testid="bank-hakili-regard-fait">
      <p className="font-medium">
        {fill(look.accord ? "bank.hakili.regardFaitOui" : "bank.hakili.regardFaitNon", {
          nom: look.byName,
          quand: when(look.actedAt),
        })}
      </p>
      {look.disagreements.map((one) => (
        <p key={`${one.component}-${one.motif}`} className="mt-0.5 text-sm text-gray-500">
          {secondLookLine(componentLabel(one.component), motifWords(one.motif), one.phrase)}
        </p>
      ))}
      {look.count > 1 ? (
        <p className="mt-0.5 text-sm text-gray-500">
          {fill("bank.hakili.regardFaitNombre", { n: String(look.count) })}
        </p>
      ) : null}
    </div>
  );
}

/**
 * `SECOND-LOOK-CARRY`: her second look on a grade the bank's new policy changed, shown as her earlier word, labelled and awaiting
 * her confirmation. It is not counted and it does not stand on the new grade.
 */
export function EarlierSecondLook(props: {
  readonly look: AnalysisSecondLookView;
  readonly onBay?: boolean;
}): ReactElement {
  const words = fill(props.look.accord ? "bank.hakili.regardFaitOui" : "bank.hakili.regardFaitNon", {
    nom: props.look.byName,
    quand: when(props.look.actedAt),
  });
  const line = fill("bank.hakili.regardAncienne", { regard: words });
  return props.onBay === true ? (
    <p data-bay="regard-ligne" data-testid="bank-hakili-regard-ancien">
      {line}
    </p>
  ) : (
    <p className="mb-3 text-sm font-medium text-amber-700" data-testid="bank-hakili-regard-ancien">
      {line}
    </p>
  );
}

/**
 * `OD-37` round 10b (§9.4, D-4): **« Êtes-vous d'accord avec cette note ? »** on the grade card. Her answer goes by her
 * own door with her name from her session; the card sends no author. The recorded look is shown with her name and the
 * date. Nothing here changes the letter: the look is stored beside it, for the grille's calibration.
 */
export function SecondLook(props: {
  readonly components: readonly { readonly key: string }[];
  /** `ARC2-BAY`: the chef's read, which has no form: with no look recorded it says so (D2 `bay.js` secondLook). */
  readonly readOnly?: boolean;
  readonly look: AnalysisSecondLookView | null;
  /** `SECOND-LOOK-CARRY`: her look on the grade before the policy changed it. */
  readonly earlier?: AnalysisSecondLookView | null;
  readonly onSecondLook?: (body: SecondLookBody) => Promise<void>;
}): ReactElement | null {
  const [refusal, setRefusal] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const { look, onSecondLook } = props;
  const earlier = props.earlier ?? null;
  if (look === null && earlier === null && onSecondLook === undefined) {
    return props.readOnly === true ? (
      <p data-bay="meta" data-testid="bank-hakili-regard-aucune">
        {t("bank.hakili.regardAucune")}
      </p>
    ) : null;
  }
  // `ARC2-BAY`, `ARC2-DATA` item 1 (D2 `bay.js` secondLook): each button IS the answer, sent at the click. « Pas d'accord » carries no component
  // and no motif (the door takes it with an empty list): no form, no save, no reason to choose.
  const transmit = (body: SecondLookBody): void => {
    if (onSecondLook === undefined) return;
    setSending(true);
    setRefusal(null);
    onSecondLook(body)
      .catch((error: unknown) => {
        setRefusal(error instanceof Error ? error.message : String(error));
      })
      .finally(() => {
        setSending(false);
      });
  };
  return (
    <div data-bay="regard" {...(onSecondLook === undefined ? {} : { "data-testid": "bank-hakili-regard" })}>
      {look === null ? null : <SecondLookRecord look={look} onBay />}
      {earlier === null || look !== null ? null : <EarlierSecondLook look={earlier} onBay />}
      {onSecondLook === undefined || look !== null ? null : (
        <div>
          <p data-bay="regard-question" data-testid="bank-hakili-regard-question">
            {t("bank.hakili.regardQuestion")}
          </p>
          <div data-bay="btn-group">
            {([true, false] as const).map((value) => (
              <Button
                key={String(value)}
                size="sm"
                variant="secondary"
                disabled={sending}
                onClick={() => {
                  transmit(value ? { accord: true } : { accord: false, disagreements: [] });
                }}
                data-testid={`bank-hakili-regard-accord-${value ? "oui" : "non"}`}
              >
                {t(value ? "bank.hakili.regardOui" : "bank.hakili.regardNon")}
              </Button>
            ))}
          </div>
          <p data-bay="meta">{t("bank.hakili.regardAide")}</p>
          {refusal === null ? null : (
            <p role="alert" data-bay="erreur" data-testid="bank-hakili-regard-refus">
              {refusal}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * `F-FLOOR-2` (D-1025): **a date is one unit on the screen.** At 14 px the avis notice wrapped between the day and the month
 * (« … a changé le 24 / octobre 2026 »). The canon names no character for the spaces of a date (`06-cross-cutting.md:1203`, `d MMMM yyyy`),
 * so the join binds them with U+00A0, here, where the notice prints (the words stay the server's, byte for byte, apart from those two
 * spaces; other screens' dates are `FRENCH-SPACING`'s).
 */
const DATE_IN_WORDS = /(\d{1,2}|1er) ([\p{Ll}]+) (\d{4})/gu;
const bindDate = (words: string): string => words.replace(DATE_IN_WORDS, "$1\u00A0$2\u00A0$3");

/**
 * « Avis · Favorable », **in the bay** (`F-2`, `ARC2-BAY`): the recommendation's word and Hakili's text as ONE paragraph, in every context
 * (D2 `bay.js` avisBlock draws no field: the analyst does not edit it here). A notice, and the analyst's recorded act if there is one, follow.
 */
export function OpinionPanel(props: {
  readonly opinion: AnalysisOpinionView | null;
  readonly sentence: string | null;
  readonly busy: boolean;
  /** `CHEF-BAY` (triage row 62): the decision's read also prints the analyst's corrected text as « Avis retenu par l'analyste ». */
  readonly readOnly?: boolean;
}): ReactElement {
  return (
    <section data-bay="avis" data-testid="bank-hakili-avis">
      {/* D2 `bay.js` avisBlock: « Avis · Favorable », the recommendation's word in the bay's green, then the text. No icon, no separate chip. */}
      <h3 data-bay="h3">
        {t("bank.hakili.avisTitre")}
        {props.opinion === null ? null : (
          <>
            {" · "}
            <span data-bay="rec" data-reco={props.opinion.recommendation} data-testid="bank-hakili-avis-chip">
              {t(RECOMMENDATION_KEYS.get(props.opinion.recommendation) ?? "bank.hakili.avisReserve")}
            </span>
          </>
        )}
      </h3>
      {props.opinion === null ? (
        <p data-bay="note" data-testid="bank-hakili-avis-absent">
          {props.sentence ?? t("bank.hakili.avisAbsent")}
        </p>
      ) : (
        <>
          {props.opinion.notice === undefined || props.opinion.notice === null ? null : (
            <p
              className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-700"
              role="status"
              data-testid="bank-hakili-avis-a-relire"
            >
              {bindDate(props.opinion.notice)}
            </p>
          )}
          {props.opinion.superseded === undefined || props.opinion.superseded === null ? null : (
            <p
              className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-700"
              role="status"
              data-testid="bank-hakili-avis-superseded"
            >
              {props.opinion.superseded}
            </p>
          )}
          <p data-bay="avis-texte" data-testid="bank-hakili-avis-texte">
            {props.opinion.text}
          </p>
          {props.opinion.act === null ? null : (
            <p data-bay="meta" data-testid="bank-hakili-avis-acte">
              {props.readOnly === true && props.opinion.act.act === "corrigee"
                ? fill("bank.decision.avisCorrigePar", {
                    nom: props.opinion.act.byName,
                    quand: when(props.opinion.act.actedAt),
                  })
                : actWords(props.opinion.act)}
            </p>
          )}
          {props.readOnly === true &&
          props.opinion.act?.act === "corrigee" &&
          props.opinion.act.value !== null ? (
            <div data-bay="avis-analyste" data-testid="bank-hakili-avis-analyste">
              <b data-bay="avis-retenu-label">{t("bank.decision.avisAnalyste")}</b>
              <p data-bay="avis-retenu-texte">{props.opinion.act.value}</p>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * « Voir l'analyse de Hakili »: what Hakili did, in French, and when. **The model, the prompt, the rule and the run's id are
 * the stored record's and the staff console's** , the bank's projection (`BANK-IDS`) does not carry them, and this
 * list could not print them if it tried. `defaultOpen` is the nouveau file's trace, always in view.
 */
export function AnalysisTrail(props: {
  readonly analysis: AnalysisView;
  readonly defaultOpen?: boolean;
  /**
   * `F-2c` (X2): the trail on the work column's banner, outside the bay's kit whose rules style it there. It wears the work
   * column's own classes (the chevron beside the words, 14 px) and the bay's mount passes nothing.
   */
  readonly onWork?: boolean;
}): ReactElement {
  const [open, setOpen] = useState(props.defaultOpen === true);
  const work = props.onWork === true;
  // `ARC2-DATA` (item 3): the first `points` step is the reading of the pièces; a later step of the same kind keeps its own end.
  const readingOrdinal = props.analysis.steps.find((one) => one.kind === "points")?.ordinal ?? null;
  const readingAt = props.analysis.run.documentsReadAt;
  const readingTime = readingAt === null || Number.isNaN(Date.parse(readingAt)) ? null : when(readingAt);
  return (
    <div data-bay="trace" data-testid="bank-hakili-trail">
      <button
        type="button"
        onClick={() => {
          setOpen(!open);
        }}
        aria-expanded={open}
        data-bay="trace-ouvrir"
        data-testid="bank-hakili-trail-ouvrir"
        className={
          work ? "mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-indigo-700" : undefined
        }
      >
        {open ? (
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        )}
        {t("bank.hakili.voirAnalyse")}
      </button>
      {open ? (
        <ol
          data-bay="trace-etapes"
          data-testid="bank-hakili-trail-etapes"
          className={work ? "mt-1 space-y-1 text-sm text-gray-700" : undefined}
        >
          {props.analysis.steps.map((step) => (
            <li
              key={step.ordinal}
              data-state={step.failure === null ? "on" : "failed"}
              data-testid={`bank-hakili-etape-${String(step.ordinal)}`}
            >
              <span>{t(STEP_KEYS.get(step.kind) ?? "bank.hakili.faitPoints")}</span>
              {/* `ARC2-DATA` (item 3): the reading line wears the instant the pièces were really read, as the door gives it (the origin instant of the
                  readings the run consumed), never the step's end, which is the run's clock or a copy's. Where the door does not know it, no time. */}
              {step.ordinal === readingOrdinal ? (
                readingTime === null ? null : (
                  <>
                    {" · "}
                    <time>{readingTime}</time>
                  </>
                )
              ) : (
                <>
                  {" · "}
                  <time>{when(step.endedAt)}</time>
                </>
              )}
              {step.failure === null ? null : (
                <p
                  data-bay="erreur"
                  data-testid={`bank-hakili-etape-${String(step.ordinal)}-echec`}
                  className={work ? "text-sm text-rose-700" : undefined}
                >
                  {step.failure}
                </p>
              )}
            </li>
          ))}
        </ol>
      ) : null}
      {/* REVUE-CLEAN: the readings behind the note fold in here, closed by default; the bay's own trail does not carry them. */}
      {open && work ? <ReadingsCard analysis={props.analysis} /> : null}
    </div>
  );
}

/**
 * `OD-37` round 9c-2b (L-6): the readings behind the number. A white card, a 1 px border, no shadow: each reading with
 * its operands, the two capacities side by side, the charges, the reste à vivre and the offer's term note.
 */
export function ReadingsCard(props: { readonly analysis: AnalysisView | null }): ReactElement | null {
  const analysis = props.analysis;
  const card = analysis?.card ?? null;
  if (analysis === null || card === null) return null;
  const feesAverage = card.fees?.average ?? null;
  return (
    <div
      className="mb-3 rounded border border-gray-200 bg-white p-3 text-sm text-gray-700"
      data-testid="bank-carte-lectures"
    >
      <p className="mb-1 font-medium">{t("bank.hakili.carte_titre")}</p>
      <ul>
        {card.readings.map((one) => (
          <li key={one.code} data-testid={`bank-carte-lecture-${one.code}`}>
            <span>{one.reason}</span>
            {one.operands.map((operand) => (
              <span key={operand} className="block text-gray-500">
                {operand}
              </span>
            ))}
          </li>
        ))}
      </ul>
      <p data-testid="bank-carte-capacites">
        {t("bank.hakili.carte_capaciteDossier")}{" "}
        {card.capacityAtFile === null ? DASH : formatMoney(card.capacityAtFile)}
        {" ; "}
        {t("bank.hakili.carte_capaciteObservee")}{" "}
        {card.capacityObserved === null ? DASH : formatMoney(card.capacityObserved.amount)}
      </p>
      {feesAverage === null ? null : (
        <p data-testid="bank-carte-charges">
          {t("bank.hakili.carte_charges")} {formatMoney(feesAverage)}
          {(card.fees?.perMonth ?? []).length === 0
            ? ""
            : ` (${(card.fees?.perMonth ?? []).map((one) => `${one.label} ${formatMoney(one.total)}`).join(" ; ")})`}
        </p>
      )}
      {card.resteAVivre === null ? null : (
        <p data-testid="bank-carte-reste-a-vivre">
          {t("bank.hakili.carte_resteAVivre")} {formatMoney(card.resteAVivre.recommended.amount)} (
          {card.resteAVivre.recommended.sharePct} %) : {card.resteAVivre.recommended.operands}
        </p>
      )}
      {card.resteAVivre?.recommended.superseded === undefined ||
      card.resteAVivre.recommended.superseded === null ? null : (
        <p
          className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-700"
          role="status"
          data-testid="bank-carte-reste-a-vivre-superseded"
        >
          {card.resteAVivre.recommended.superseded}
        </p>
      )}
      {analysis.termNote === null ? null : <p data-testid="bank-carte-duree">{analysis.termNote}</p>}
      {analysis.offer === null && analysis.offerReason !== null ? (
        <p data-testid="bank-carte-sans-offre">{analysis.offerReason}</p>
      ) : null}
    </div>
  );
}

/**
 * The analysis's status for the work column, to D2 frames 11, 12 and 24: a run still going is the info callout, a failed run the warn
 * callout (« L'analyse de Hakili n'a pas abouti. La revue se fait à la main : … »), no analysis at all the same warn callout with R81's
 * sentence. The server's own failure sentence is the bay's (D2 puts it in the right rail), not this column's.
 */
export const COUNT_WORDS = new Map<number, string>([
  [15, "quinze"],
  [16, "seize"],
]);

export function AnalysisBanner(props: {
  readonly analysis: AnalysisView | null;
  readonly sentence: string | null;
  /** The door's own number of points, for « les quinze points » (D2); absent, the figure in digits. */
  readonly pointCount?: number;
}): ReactElement | null {
  if (props.analysis === null) {
    return (
      <Callout
        tone="warn"
        icon={<AlertTriangle className="h-5 w-5" aria-hidden="true" />}
        className="mb-4"
        testId="bank-hakili-absente"
      >
        {props.sentence ?? t("bank.hakili.aucuneAnalyse")}
      </Callout>
    );
  }
  if (props.analysis.run.outcome === "en_echec") {
    const count = props.pointCount ?? 0;
    return (
      <Callout
        tone="warn"
        icon={<AlertTriangle className="h-5 w-5" aria-hidden="true" />}
        className="mb-4"
        testId="bank-hakili-echec"
      >
        <b>{t("bank.hakili.echecTitre")}</b>{" "}
        {fill("bank.hakili.echecSuite", { n: COUNT_WORDS.get(count) ?? String(count) })}
      </Callout>
    );
  }
  if (props.analysis.run.outcome === null) {
    return (
      <Callout
        tone="info"
        icon={<Info className="h-5 w-5" aria-hidden="true" />}
        className="mb-4"
        testId="bank-hakili-en-cours"
      >
        <b>{t("bank.hakili.enCoursTitre")}</b> {t("bank.hakili.enCoursSuite")}
      </Callout>
    );
  }
  // D2 draws no card above the tabs once the run is done: the proposals are in the table, and « Voir l'analyse de Hakili » is the bay's.
  return null;
}
